import { RpcError } from '@protobuf-ts/runtime-rpc'
import { SimpleConfirmationDialog } from 'components/core/confirmation/dialog/SimpleConfirmationDialog'
import { C1Link } from 'components/core/links/C1Link'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useAlert } from 'hooks/useAlert'
import { TaskValue } from 'lib/actions/task'
import { routes } from 'lib/routes'
import { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

export type ProcessNowDialogV2Props = {
    task: TaskValue
} & ClosableProps

export const ProcessNowTaskDialogV2: FC<ProcessNowDialogV2Props> = ({ task, close }) => {
    const intl = useIntl()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const handleClick = useCallback(async (): Promise<void> => {
        try {
            await ticketActionTracker?.processNow(task)
        } catch (err: unknown) {
            return toast(
                <FormattedMessage
                    id="task_process_now_error"
                    defaultMessage="Error processing task {task_link} now. {code}: {msg}"
                    values={{
                        task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    }}
                />,
                'error',
            )
        }

        toast(
            <FormattedMessage
                id="task_process_now_success"
                defaultMessage="Task {task_link} processed successfully"
                values={{
                    task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                }}
            />,
            'success',
        )

        close()
    }, [close, task, ticketActionTracker, toast])

    return (
        <SimpleConfirmationDialog
            submitActionLabel={intl.formatMessage({
                id: 'process_now_confirmation_dialog_action_string',
                defaultMessage: 'Process now',
            })}
            title={intl.formatMessage({
                id: 'process_now_confirmation_dialog_title',
                defaultMessage: 'Are you sure you want to process this task now?',
            })}
            details={intl.formatMessage({
                id: 'process_now_confirmation_dialog_description',
                defaultMessage:
                    'Kick off the processing of this task now.',
            })}
            onConfirm={handleClick}
            onCancel={close}
            inProgress={!ticketActionTracker}
            close={close}
        />
    )
}
